<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="displayLocations"
      :loading="loading"
      :item-class="getItemClass"
      item-key="id"
      no-data-text="Không có vị trí nào"
      :footer-props="{
        'items-per-page-text': 'Hiển thị mỗi trang',
        'items-per-page-all-text': 'Tất cả',
        'items-per-page-options': locations.length > 100 ? [10, 20, 50] : [10, 20, -1]
      }"
      disable-sort
    >
      <!-- Cột 1: Tên vị trí hiển thị cây đa cấp -->
      <template v-slot:item.name="{ item }">
        <div
          class="d-flex align-center location-name-cell"
          :style="{ paddingLeft: item.isChildInTree ? (item.treeDepth - 1) * 14 + 'px' : '0px' }"
        >
          <!-- Đường kẻ phân nhánh ngắn cho vị trí con -->
          <span v-if="item.isChildInTree" class="tree-branch-line"></span>

          <!-- Tên vị trí -->
          <router-link
            :to="'/facility/assets?location=' + item.id"
            class="text-decoration-none mr-2 primary--text"
            title="Xem các thiết bị tại vị trí này"
          >
            {{ item.name }}
          </router-link>
        </div>
      </template>

      <!-- Cột 2: Mã vị trí -->
      <template v-slot:item.code="{ item }">
        <span>{{ item.code }}</span>
      </template>

      <!-- Cột 3: Loại cấp bậc dạng thường căn trái -->
      <template v-slot:item.type="{ item }">
        <span>{{ item.type | locationType }}</span>
      </template>

      <!-- Cột 4: Vị trí cấp trên -->
      <template v-slot:item.parent="{ item }">
        <span>{{ (item.parent && item.parent.name) || '-' }}</span>
      </template>

      <!-- Cột 5: Thẻ đính kèm (Cấp học) dạng Chip có Tooltip khi hover -->
      <template v-slot:item.academicLevel="{ item }">
        <div v-if="getItemTags(item).length" class="d-flex flex-wrap align-center">
          <v-tooltip v-for="(tag, idx) in getItemTags(item)" :key="idx" top>
            <template v-slot:activator="{ on, attrs }">
              <v-chip
                v-bind="attrs"
                v-on="on"
                small
                :color="getTagColor(tag)"
                dark
                class="font-weight-medium my-1 mr-1"
                style="cursor: pointer"
              >
                {{ tag }}
              </v-chip>
            </template>
            <span>{{ getTagFullName(tag) }}</span>
          </v-tooltip>
        </div>
        <span v-else class="grey--text">-</span>
      </template>

      <!-- Cột 6: Người quản lý -->
      <template v-slot:item.manager="{ item }">
        <span>{{ (item.manager && (item.manager.fullName || item.manager.username)) || '-' }}</span>
      </template>

      <!-- Cột 7: Thao tác -->
      <template v-slot:item.actions="{ item }">
        <v-btn
          icon
          small
          color="indigo"
          :to="'/facility/assets?location=' + item.id"
          title="Xem danh sách thiết bị tại vị trí này"
        >
          <v-icon small>mdi-devices</v-icon>
        </v-btn>
        <v-btn icon small color="primary" @click="onEdit(item)" title="Sửa vị trí">
          <v-icon small>mdi-pencil</v-icon>
        </v-btn>
        <v-btn icon small color="error" @click="onRemove(item)" title="Xóa vị trí">
          <v-icon small>mdi-delete</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <!-- Dialog sửa vị trí -->
    <LocationEditDialog :state="editDialog" :locationData="selectedItem" @updated="refresh()" />
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import LocationEditDialog from '@/modules/facility/location/LocationEditDialog.vue'

const headers = [
  { text: 'Tên vị trí', value: 'name', align: 'left', sortable: false },
  { text: 'Mã vị trí', value: 'code', align: 'left', sortable: false, width: '130px' },
  { text: 'Loại', value: 'type', align: 'left', sortable: false, width: '120px' },
  { text: 'Vị trí cấp trên', value: 'parent', align: 'left', sortable: false },
  { text: 'Thẻ', value: 'academicLevel', align: 'left', sortable: false },
  { text: 'Người quản lý', value: 'manager', align: 'left', sortable: false },
  { text: 'Thao tác', value: 'actions', align: 'left', sortable: false, width: '130px' }
]

export default {
  name: 'LocationDataTable',
  components: {
    LocationEditDialog
  },
  data() {
    return {
      headers,
      editDialog: false,
      selectedItem: null
    }
  },
  computed: {
    ...mapState('FacilityLocations', ['locations', 'totalItems', 'pageText', 'loading']),
    displayLocations() {
      if (!this.locations || !this.locations.length) return []

      const itemMap = new Map()
      const childrenMap = new Map()
      const rootItems = []

      // Index all items
      this.locations.forEach(item => {
        itemMap.set(item.id, item)
        childrenMap.set(item.id, [])
      })

      // Group children by parent
      this.locations.forEach(item => {
        const parentId = item.parent && (item.parent.id || item.parent)
        if (parentId && itemMap.has(parentId)) {
          childrenMap.get(parentId).push(item)
        } else {
          // Standalone / Root item (e.g. Building or when parent not in filtered search)
          rootItems.push(item)
        }
      })

      const result = []

      const traverse = (item, treeDepth = 1, isChildInTree = false) => {
        const children = childrenMap.get(item.id) || []
        const displayLevel = item.level ? item.level : item.parent ? 2 : 1

        result.push({
          ...item,
          displayLevel,
          treeDepth,
          isChildInTree
        })

        // Recursively append children right under their parent
        children.forEach(child => {
          traverse(child, treeDepth + 1, true)
        })
      }

      rootItems.forEach(root => {
        traverse(root, 1, false)
      })

      return result
    }
  },
  async created() {
    await this.refresh()
  },
  methods: {
    ...mapActions('FacilityLocations', ['requestPageSettings', 'searchLocationList', 'removeLocation']),
    async refresh(query) {
      this.searchLocationList(query || {})
    },
    getTagColor(tag) {
      const title = String(tag || '').toLowerCase()
      return 'primary'
    },
    getItemTags(item) {
      if (!item) return []
      if (Array.isArray(item.tags) && item.tags.length) {
        return item.tags.filter(Boolean)
      }
      if (typeof item.tags === 'string' && item.tags.trim()) {
        return item.tags.includes(',')
          ? item.tags
              .split(',')
              .map(t => t.trim())
              .filter(Boolean)
          : [item.tags.trim()]
      }
      if (item.academicLevel) {
        const alTitle =
          (item.academicLevel && (item.academicLevel.title || item.academicLevel.name)) || item.academicLevel
        if (alTitle) return [String(alTitle)]
      }
      return []
    },
    getTagFullName(tag) {
      if (!tag) return ''
      const title = String(tag).trim()
      if (title.toLowerCase() === 'cấp 1' || title.toLowerCase().includes('tiểu học')) return `${title} (Tiểu học)`
      if (title.toLowerCase() === 'cấp 2' || title.toLowerCase().includes('thcs'))
        return `${title} (Trung học cơ sở - THCS)`
      if (title.toLowerCase() === 'cấp 3' || title.toLowerCase().includes('thpt'))
        return `${title} (Trung học phổ thông - THPT)`
      return title
    },
    getItemClass(item) {
      if (item.isChildInTree) {
        return 'child-location-row'
      }
      return 'parent-location-row'
    },
    onEdit(item) {
      this.selectedItem = item
      this.editDialog = !this.editDialog
    },
    onRemove(item) {
      this.$dialog.confirm({
        title: 'Thông báo xác nhận',
        text: `Bạn có chắc muốn xóa vị trí "${item.name}"? Bạn không thể hoàn tác hành động.`,
        okText: 'Xóa',
        cancelText: 'Hủy',
        done: async () => {
          this.$loading.active = true
          try {
            await this.removeLocation(item.id)
            this.$alert.deleteSuccess()
          } catch (error) {
            this.$alert.deleteError(error)
          } finally {
            this.$loading.active = false
          }
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.location-name-cell {
  min-height: 36px;
}

.tree-branch-line {
  display: inline-block;
  width: 8px;
  height: 10px;
  border-left: 1.5px solid #94a3b8;
  border-bottom: 1.5px solid #94a3b8;
  margin-right: 6px;
  margin-top: -5px;
  flex-shrink: 0;
  user-select: none;
}

::v-deep tr.child-location-row {
  background-color: #fafbfc;
  &:hover {
    background-color: #f1f5f9 !important;
  }
}

::v-deep tr.parent-location-row {
  background-color: #ffffff;
}
</style>
